import { create } from 'zustand';
import {
    getCurriculumApproach,
    getQuestionsForApproach,
    getRecommendationProfile,
    scoreKBItemForProfile,
    generateAssessmentSummary
} from '../soloAssessmentLogic';

/**
 * Learning Profile Store
 * Manages state for creating/editing learning profiles in the Solo platform
 */
export const useLearningProfileStore = create((set, get) => ({
    // Profile identification
    currentProfileId: null, // null = creating new, string = editing existing
    profileName: '',

    // Step tracking
    step: 'profile', // 'profile' | 'questions' | 'results'

    // Profile answers (Phase 1)
    profileAnswers: {
        ageGroup: null,
        cognitiveLevel: null
    },

    // Questionnaire answers (Phase 2)
    questionnaireAnswers: {},
    currentQuestionIndex: 0,

    // Results (Phase 3)
    curriculumApproach: null,
    recommendedActivities: [],
    assessmentSummary: null,

    // Loading state
    isLoading: false,
    isSaving: false,

    // ========== ACTIONS ==========

    // Profile answers
    setProfileAnswer: (key, value) => {
        set(state => ({
            profileAnswers: {
                ...state.profileAnswers,
                [key]: value
            }
        }));
    },

    // Moving between steps
    setStep: (step) => set({ step }),

    proceedToQuestions: () => {
        const { profileAnswers } = get();
        const approach = getCurriculumApproach(profileAnswers);
        set({
            step: 'questions',
            curriculumApproach: approach,
            currentQuestionIndex: 0
        });
    },

    // Questionnaire answers
    setQuestionnaireAnswer: (questionId, value) => {
        set(state => ({
            questionnaireAnswers: {
                ...state.questionnaireAnswers,
                [questionId]: value
            }
        }));
    },

    nextQuestion: () => {
        const { currentQuestionIndex, curriculumApproach } = get();
        const questions = getQuestionsForApproach(curriculumApproach);
        if (currentQuestionIndex < questions.length - 1) {
            set({ currentQuestionIndex: currentQuestionIndex + 1 });
        }
    },

    prevQuestion: () => {
        const { currentQuestionIndex } = get();
        if (currentQuestionIndex > 0) {
            set({ currentQuestionIndex: currentQuestionIndex - 1 });
        }
    },

    goToQuestion: (index) => {
        set({ currentQuestionIndex: index });
    },

    // Generate results
    generateResults: async (kbItems) => {
        const { profileAnswers, questionnaireAnswers } = get();

        set({ isLoading: true });

        try {
            // Build recommendation profile
            const profile = getRecommendationProfile(profileAnswers, questionnaireAnswers);

            // Score all KB items
            const scoredItems = kbItems
                .map(item => ({
                    ...item,
                    matchScore: scoreKBItemForProfile(profile, item)
                }))
                .filter(item => item.matchScore >= 0.3)
                .sort((a, b) => b.matchScore - a.matchScore)
                .slice(0, 10);

            // Generate summary
            const summary = generateAssessmentSummary(profile, profileAnswers);

            set({
                recommendedActivities: scoredItems,
                assessmentSummary: summary,
                step: 'results',
                isLoading: false
            });

            return { profile, scoredItems, summary };
        } catch (error) {
            console.error('Error generating results:', error);
            set({ isLoading: false });
            throw error;
        }
    },

    // Edit mode - return to questions with answers preserved
    editProfile: () => {
        set({ step: 'questions', currentQuestionIndex: 0 });
    },

    // Profile name
    setProfileName: (name) => set({ profileName: name }),

    // Set current profile ID (for editing existing profiles)
    setCurrentProfileId: (id) => set({ currentProfileId: id }),

    // Load existing profile for editing
    loadProfile: (profileData) => {
        set({
            currentProfileId: profileData.id,
            profileName: profileData.name || '',
            profileAnswers: profileData.profileAnswers || { ageGroup: null, cognitiveLevel: null },
            questionnaireAnswers: profileData.questionnaireAnswers || {},
            curriculumApproach: profileData.curriculumApproach || null,
            assessmentSummary: profileData.summary || null,
            step: 'profile',
            currentQuestionIndex: 0
        });
    },

    // Reset store for new profile
    reset: () => {
        set({
            currentProfileId: null,
            profileName: '',
            step: 'profile',
            profileAnswers: {
                ageGroup: null,
                cognitiveLevel: null
            },
            questionnaireAnswers: {},
            currentQuestionIndex: 0,
            curriculumApproach: null,
            recommendedActivities: [],
            assessmentSummary: null,
            isLoading: false,
            isSaving: false
        });
    },

    // ========== COMPUTED / HELPERS ==========

    getCurrentQuestions: () => {
        const { curriculumApproach } = get();
        if (!curriculumApproach) return [];
        return getQuestionsForApproach(curriculumApproach);
    },

    getCurrentQuestion: () => {
        const { currentQuestionIndex } = get();
        const questions = get().getCurrentQuestions();
        return questions[currentQuestionIndex] || null;
    },

    isProfileComplete: () => {
        const { profileAnswers } = get();
        return profileAnswers.ageGroup && profileAnswers.cognitiveLevel;
    },

    isQuestionnaireComplete: () => {
        const { questionnaireAnswers, curriculumApproach } = get();
        if (!curriculumApproach) return false;

        const questions = getQuestionsForApproach(curriculumApproach);
        return questions.every(q => questionnaireAnswers[q.id] !== undefined);
    },

    getProgress: () => {
        const { step, currentQuestionIndex, curriculumApproach, questionnaireAnswers } = get();

        if (step === 'profile') return { current: 1, total: 3, label: 'Basic Profile' };
        if (step === 'results') return { current: 3, total: 3, label: 'Results' };

        const questions = curriculumApproach ? getQuestionsForApproach(curriculumApproach) : [];
        const answered = Object.keys(questionnaireAnswers).length;

        return {
            current: 2,
            total: 3,
            label: 'Questions',
            questionProgress: {
                current: currentQuestionIndex + 1,
                total: questions.length,
                answered
            }
        };
    },

    // Get data for saving to Firestore
    getProfileData: () => {
        const {
            profileName,
            profileAnswers,
            questionnaireAnswers,
            curriculumApproach,
            recommendedActivities,
            assessmentSummary
        } = get();

        return {
            name: profileName,
            profileAnswers,
            questionnaireAnswers,
            curriculumApproach,
            recommendedActivityIds: recommendedActivities.map(a => a.id),
            summary: assessmentSummary
        };
    }
}));
